import { createContext, useContext, useState, type ReactNode } from "react"
import chatgptLogo from "@/assets/chatgptlogo.png"
import chatgptLogoWhite from "@/assets/chatgpt_logo_white.png"
import geminiLogo from "@/assets/geminilogo.png"
import claudeLogo from "@/assets/claudelogo.png"

export type LLMProvider = "openai" | "google" | "anthropic"

export interface LLMProviderInfo {
  id: LLMProvider
  name: string
  description: string
  icon: string
  getIcon?: (isDark: boolean) => string
}

export const LLM_PROVIDERS: LLMProviderInfo[] = [
  {
    id: "openai",
    name: "ChatGPT",
    description: "OpenAI GPT-4o",
    icon: chatgptLogo,
    getIcon: (isDark: boolean) => isDark ? chatgptLogoWhite : chatgptLogo
  },
  {
    id: "google", 
    name: "Gemini",
    description: "Google Gemini 2.5 Flash",
    icon: geminiLogo
  },
  {
    id: "anthropic",
    name: "Claude",
    description: "Anthropic Claude Sonnet 4",
    icon: claudeLogo
  }
]

interface LLMContextType {
  selectedProvider: LLMProvider
  setSelectedProvider: (provider: LLMProvider) => void
  getProviderInfo: (provider: LLMProvider) => LLMProviderInfo | undefined
}

const LLMContext = createContext<LLMContextType | undefined>(undefined)

interface LLMProviderProps {
  children: ReactNode
  defaultProvider?: LLMProvider
}

export function LLMContextProvider({ children, defaultProvider = "google" }: LLMProviderProps) {
  const [selectedProvider, setSelectedProvider] = useState<LLMProvider>(defaultProvider)

  const getProviderInfo = (provider: LLMProvider) => {
    return LLM_PROVIDERS.find(p => p.id === provider)
  }

  return (
    <LLMContext.Provider
      value={{
        selectedProvider,
        setSelectedProvider,
        getProviderInfo
      }}
    >
      {children}
    </LLMContext.Provider>
  )
}

export function useLLM() {
  const context = useContext(LLMContext)
  if (context === undefined) {
    throw new Error("useLLM must be used within a LLMContextProvider")
  }
  return context
} 